<template>
  <div class="stock-view-page">
    <StockDocumentPageHeading
      :title="$t('STOCK.DELIVERY_NOTES.PAGE_TITLE')"
      :code="document.code"
    />
    <v-card :loading="isProcessing" :disabled="isProcessing">
      <v-tabs v-model="tab" class="stock-color" left dark>
        <v-tab href="#header">{{ document.code }}</v-tab>
        <v-spacer />
        <div class="buttons-tab d-flex align-center flex-wrap pa-2">
          <v-chip v-if="document.status" class="mr-2" :color="statusColor" small dark>
            {{ statusLabel }}
          </v-chip>
          <v-btn v-if="canConfirm" class="mr-2 success" small @click="confirmNote">
            {{ $t("STOCK.DELIVERY_NOTES.CONFIRM") }}
          </v-btn>
          <v-btn v-if="canExecute" class="mr-2 primary" small @click="executeNote">
            {{ isLeadingPair ? $t("STOCK.DELIVERY_NOTES.RECORD_DIRECT_DELIVERY") : $t("STOCK.DELIVERY_NOTES.EXECUTE") }}
          </v-btn>
          <v-btn v-if="canInvoice" class="mr-2 primary" small @click="openInvoiceDialog">
            {{ $t("STOCK.DELIVERY_NOTES.CREATE_INVOICE") }}
          </v-btn>
          <v-btn v-if="canTransport" class="mr-2 primary" small @click="createTransport">
            {{ $t("STOCK.DELIVERY_NOTES.CREATE_TRANSPORT") }}
          </v-btn>
          <v-btn v-if="canCancel" class="mr-2 error" small @click="cancelNote">
            {{ $t("GENERAL.LABELS.CANCEL") }}
          </v-btn>
          <v-btn v-if="canEditNote" class="mr-2 success" small @click="save">
            {{ $t("GENERAL.LABELS.SAVE") }}
          </v-btn>
        </div>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item value="header">
          <v-card flat>
            <v-card-text>
              <v-alert v-if="document.is_intercompany_mirror" type="info" outlined dense class="mb-4">
                {{ mirrorHint }}
              </v-alert>
              <v-alert v-if="transportCovered" type="info" outlined dense class="mb-4">
                <template v-if="document.covering_transport_order">
                  {{ $t("STOCK.DELIVERY_NOTES.TRANSPORT_COVERED") }}
                  <router-link
                    :to="{ name: 'stock-transport-orders-view', params: { id: document.covering_transport_order.id } }"
                  >{{ document.covering_transport_order.code }}</router-link>.
                </template>
                <template v-else>{{ $t("STOCK.DELIVERY_NOTES.TRANSPORT_COVERED_PENDING") }}</template>
              </v-alert>
              <v-alert
                v-if="document.paired_delivery_note"
                :type="isLeadingPair ? 'info' : 'warning'"
                outlined
                dense
                class="mb-4"
              >
                <v-chip x-small outlined color="primary" class="mr-1">
                  {{ $t("STOCK.DELIVERY_NOTES.DIRECT_TO_CUSTOMER") }}
                </v-chip>
                <v-chip v-if="isLeadingPair" x-small color="primary" dark class="mr-2">
                  {{ $t("STOCK.DELIVERY_NOTES.DROPSHIP_LEADING") }}
                </v-chip>
                <template v-if="isLeadingPair">{{ $t("STOCK.DELIVERY_NOTES.DROPSHIP_LEADING_ALERT") }}</template>
                <template v-else>{{ $t("STOCK.DELIVERY_NOTES.DROPSHIP_COMMERCIAL_ALERT") }}</template>
                <router-link v-if="pairedRoute" :to="pairedRoute">{{ document.paired_delivery_note.code }}</router-link>
                <template v-else>{{ document.paired_delivery_note.code }}</template>
                <span v-if="pairedRole"> · {{ pairedRole }}</span>
                <span v-if="document.paired_delivery_note.order_code"> · {{ document.paired_delivery_note.order_code }}</span>
                <span v-if="pairedStatus"> · {{ pairedStatus }}</span>
                <div class="caption mt-1">{{ $t("STOCK.DELIVERY_NOTES.DROPSHIP_PAIR_HINT") }}</div>
              </v-alert>
              <div class="dn-facts">
                <div class="dn-fact">
                  <v-icon class="dn-fact__icon">mdi-swap-horizontal</v-icon>
                  <div>
                    <div class="dn-fact__label">
                      {{ $t("STOCK.DELIVERY_NOTES.DIRECTION") }}
                      <v-icon x-small>mdi-lock-outline</v-icon>
                    </div>
                    <div class="dn-fact__value">{{ directionLabel || "—" }}</div>
                  </div>
                </div>
                <div class="dn-fact">
                  <v-icon class="dn-fact__icon">mdi-file-document-outline</v-icon>
                  <div>
                    <div class="dn-fact__label">
                      {{ $t("STOCK.DELIVERY_NOTES.ORDER") }}
                      <v-icon x-small>mdi-lock-outline</v-icon>
                    </div>
                    <div class="dn-fact__value">
                      <router-link v-if="orderRoute" :to="orderRoute">{{ orderCode }}</router-link>
                      <template v-else>{{ orderCode || "—" }}</template>
                    </div>
                  </div>
                </div>
                <div v-if="deals.length" class="dn-fact">
                  <v-icon class="dn-fact__icon">mdi-briefcase-outline</v-icon>
                  <div>
                    <div class="dn-fact__label">
                      {{ $t("STOCK.DELIVERY_NOTES.DEAL") }}
                      <v-icon x-small>mdi-lock-outline</v-icon>
                    </div>
                    <div v-for="deal in deals" :key="deal.id" class="dn-fact__value">
                      <router-link :to="{ name: 'deals-view', params: { id: deal.id } }">
                        {{ dealLabel(deal) }}
                      </router-link>
                    </div>
                  </div>
                </div>
                <div class="dn-fact">
                  <v-icon class="dn-fact__icon">mdi-domain</v-icon>
                  <div>
                    <div class="dn-fact__label">
                      {{ $t("STOCK.DELIVERY_NOTES.COMPANY") }}
                      <v-icon x-small>mdi-lock-outline</v-icon>
                    </div>
                    <div class="dn-fact__value">{{ document.company?.label || document.company?.name || "—" }}</div>
                  </div>
                </div>
                <div class="dn-fact">
                  <v-icon class="dn-fact__icon">mdi-road-variant</v-icon>
                  <div>
                    <div class="dn-fact__label">
                      {{ $t("STOCK.DELIVERY_NOTES.TRANSPORT") }}
                      <v-icon x-small>mdi-lock-outline</v-icon>
                    </div>
                    <div class="dn-fact__value">
                      <router-link v-if="transportRoute" :to="transportRoute">{{ document.transport_order.code }}</router-link>
                      <template v-else>—</template>
                    </div>
                  </div>
                </div>
                <div v-if="document.counterparty_delivery_note" class="dn-fact">
                  <v-icon class="dn-fact__icon">mdi-file-swap-outline</v-icon>
                  <div>
                    <div class="dn-fact__label">
                      {{ $t("STOCK.DELIVERY_NOTES.COUNTERPARTY_DELIVERY_NOTE") }}
                      <v-icon x-small>mdi-lock-outline</v-icon>
                    </div>
                    <div class="dn-fact__value">
                      <router-link v-if="counterpartyRoute" :to="counterpartyRoute">{{ document.counterparty_delivery_note.code }}</router-link>
                      <template v-else>{{ document.counterparty_delivery_note.code }}</template>
                      <span v-if="document.counterparty_delivery_note.organization_name" class="dn-fact__meta">
                        {{ document.counterparty_delivery_note.organization_name }}
                      </span>
                    </div>
                  </div>
                </div>
                <div v-if="document.stock_action" class="dn-fact">
                  <v-icon class="dn-fact__icon">mdi-package-variant-closed</v-icon>
                  <div>
                    <div class="dn-fact__label">
                      {{ $t("STOCK.DELIVERY_NOTES.STOCK_ACTION") }}
                      <v-icon x-small>mdi-lock-outline</v-icon>
                    </div>
                    <div class="dn-fact__value">
                      <router-link :to="actionRoute">{{ document.stock_action.code }}</router-link>
                      <span class="dn-fact__meta">{{ document.stock_action.status }}</span>
                    </div>
                  </div>
                </div>
                <div v-if="linkedInvoices.length" class="dn-fact">
                  <v-icon class="dn-fact__icon">mdi-receipt-text-outline</v-icon>
                  <div>
                    <div class="dn-fact__label">
                      {{ $t("STOCK.DELIVERY_NOTES.INVOICE") }}
                      <v-icon x-small>mdi-lock-outline</v-icon>
                    </div>
                    <div v-for="invoice in linkedInvoices" :key="invoice.id" class="dn-fact__value">
                      <router-link :to="{ name: 'stock-invoices-view', params: { id: invoice.id } }">
                        {{ invoiceLabel(invoice) }}
                      </router-link>
                      <span v-if="invoiceMeta(invoice)" class="dn-fact__meta">{{ invoiceMeta(invoice) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field v-model="document.note" :label="$t('STOCK.DELIVERY_NOTES.NOTE')" :disabled="!canEditNote" />
                </v-col>
              </v-row>

              <div class="dn-route">
                <div class="dn-stop dn-stop--loading">
                  <div class="dn-stop__icon">
                    <v-icon>mdi-truck</v-icon>
                  </div>
                  <div class="dn-stop__body">
                    <div class="dn-stop__head">
                      <div class="dn-stop__label">{{ $t("STOCK.DELIVERY_NOTES.LOADING") }}</div>
                      <div class="dn-stop__date">
                        <span class="dn-stop__date-label">{{ $t("STOCK.DELIVERY_NOTES.ETD") }}</span>
                        <DatePicker
                          v-if="canEditPlan"
                          v-model="document.etd_date"
                          without-icon
                          without-hint
                          clearable
                          dense
                          hide-details
                        />
                        <span v-else class="dn-stop__date-value">{{ formatStopDate(document.etd_date) }}</span>
                      </div>
                    </div>
                    <div class="dn-stop__address">{{ document.resolved_loading_address || "—" }}</div>
                  </div>
                </div>
                <div class="dn-route__arrow" aria-hidden="true">
                  <v-icon large>mdi-arrow-right-bold</v-icon>
                </div>
                <div class="dn-stop dn-stop--unloading">
                  <div class="dn-stop__icon">
                    <v-icon>mdi-map-marker</v-icon>
                  </div>
                  <div class="dn-stop__body">
                    <div class="dn-stop__head">
                      <div class="dn-stop__label">{{ $t("STOCK.DELIVERY_NOTES.UNLOADING") }}</div>
                      <div class="dn-stop__date">
                        <span class="dn-stop__date-label">{{ $t("STOCK.DELIVERY_NOTES.ETA") }}</span>
                        <DatePicker
                          v-if="canEditPlan"
                          v-model="document.eta_date"
                          without-icon
                          without-hint
                          clearable
                          dense
                          hide-details
                        />
                        <span v-else class="dn-stop__date-value">{{ formatStopDate(document.eta_date) }}</span>
                      </div>
                    </div>
                    <div class="dn-stop__address">{{ document.resolved_unloading_address || "—" }}</div>
                  </div>
                </div>
              </div>

              <v-subheader class="px-0">{{ $t("STOCK.DELIVERY_NOTES.LINES") }}</v-subheader>
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{{ $t("STOCK.PRODUCTS.PRODUCT") }}</th>
                    <th>{{ $t("STOCK.DELIVERY_NOTES.QTY") }}</th>
                    <th>{{ $t("STOCK.DELIVERY_NOTES.WEIGHT_NET") }}</th>
                    <th>{{ $t("STOCK.DELIVERY_NOTES.WEIGHT_GROSS") }}</th>
                    <th>{{ $t("STOCK.DELIVERY_NOTES.PALLETS") }}</th>
                    <th>{{ $t("STOCK.DELIVERY_NOTES.NOTE") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(line, index) in document.items || []" :key="line.id || index">
                    <td>{{ line.line_no }}</td>
                    <td>
                      {{ line.stock_product?.label || line.stock_product?.name }}
                      <div class="caption grey--text">{{ line.stock_product?.code }}</div>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="line.quantity"
                        type="number"
                        solo
                        dense
                        hide-details
                        :suffix="line.unit?.code"
                        :disabled="!canEditPlan"
                      />
                    </td>
                    <td>
                      <v-text-field v-model.number="line.net_weight_kg" type="number" solo dense hide-details :disabled="!canEditPlan" />
                    </td>
                    <td>
                      <v-text-field v-model.number="line.gross_weight_kg" type="number" solo dense hide-details :disabled="!canEditPlan" />
                    </td>
                    <td>
                      <v-text-field v-model.number="line.pallet_count" type="number" solo dense hide-details :disabled="!canEditPlan" />
                    </td>
                    <td>
                      <v-text-field v-model="line.note" solo dense hide-details :disabled="!canEditPlan" />
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <StockDocumentFilesPanel
                v-if="document.id"
                document-kind="delivery_note"
                :document-id="document.id"
                :files="document.files || []"
                :can-regenerate="canEdit || document.status === 'confirmed' || document.status === 'executed'"
                @files-updated="document.files = $event"
              />
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <StockInvoiceLinkDialog
      v-if="document.id"
      ref="invoiceDialog"
      v-model="showInvoiceDialog"
      :delivery-note-ids="[document.id]"
      kind="delivery"
      :title="$t('STOCK.DELIVERY_NOTES.CREATE_INVOICE')"
      :hint="document.code"
      :show-pass-through="document.direction === 'outbound'"
      :submitting="isProcessing"
      @confirm="handleInvoiceConfirm"
      @error="handleInvoiceError"
    />
  </div>
</template>

<script>
import StockDocumentPageHeading from "@/views/partials/stock/StockDocumentPageHeading.vue";
import StockDocumentFilesPanel from "@/views/partials/stock/StockDocumentFilesPanel.vue";
import DatePicker from "@/views/partials/form/DatePicker.vue";
import StockInvoiceLinkDialog from "@/views/partials/stock/invoices/StockInvoiceLinkDialog.vue";
import {
  GET_STOCK_DELIVERY_NOTE,
  UPDATE_STOCK_DELIVERY_NOTE,
  CONFIRM_STOCK_DELIVERY_NOTE,
  CANCEL_STOCK_DELIVERY_NOTE,
  EXECUTE_STOCK_DELIVERY_NOTE,
  CREATE_TRANSPORT_ORDER_FROM_DELIVERY_NOTES,
  CREATE_STOCK_INVOICE_FROM_DELIVERY_NOTES,
} from "@/store/stock/stockDeliveryNotes.module";
import { SHOW_SNACK } from "@/store/snack.module";

export default {
  name: "StockViewDeliveryNote",
  components: { StockDocumentPageHeading, StockDocumentFilesPanel, DatePicker, StockInvoiceLinkDialog },
  data() {
    return {
      tab: "header",
      isProcessing: false,
      showInvoiceDialog: false,
      document: { items: [] },
    };
  },
  computed: {
    canEdit() {
      return this.document.is_editable && this.hasPermissions("stock-delivery-notes-edit");
    },
    canEditPlan() {
      return this.canEdit && !this.document.is_intercompany_mirror;
    },
    canEditNote() {
      return this.canEdit;
    },
    canConfirm() {
      return this.document.status === "draft"
        && !this.document.is_intercompany_mirror
        && this.hasPermissions("stock-delivery-notes-edit");
    },
    mirrorHint() {
      const counterparty = this.document.counterparty_delivery_note || {};
      if (counterparty.organization_name) {
        return this.$t("STOCK.DELIVERY_NOTES.INTERCOMPANY_MIRROR", {
          code: counterparty.code,
          organization: counterparty.organization_name,
        });
      }
      return this.$t("STOCK.DELIVERY_NOTES.INTERCOMPANY_MIRROR_SHORT", { code: counterparty.code || "" });
    },
    counterpartyRoute() {
      const id = this.document.counterparty_delivery_note?.id;
      return id ? { name: "stock-delivery-notes-view", params: { id } } : null;
    },
    pairedRoute() {
      const id = this.document.paired_delivery_note?.id;
      return id ? { name: "stock-delivery-notes-view", params: { id } } : null;
    },
    isLeadingPair() {
      return this.document.dropship_pair_role === "leading"
        || (this.document.paired_delivery_note && this.document.direction === "outbound");
    },
    pairedRole() {
      const direction = this.document.paired_delivery_note?.direction;
      if (direction === "outbound") {
        return this.$t("STOCK.DELIVERY_NOTES.DROPSHIP_SALES");
      }
      if (direction === "inbound") {
        return this.$t("STOCK.DELIVERY_NOTES.DROPSHIP_PURCHASE");
      }
      return "";
    },
    pairedStatus() {
      const status = this.document.paired_delivery_note?.status;
      if (!status) {
        return "";
      }
      const key = `STOCK.DELIVERY_NOTES.STATUS_${status.toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : status;
    },
    canCancel() {
      return ["draft", "confirmed"].includes(this.document.status)
        && this.hasPermissions("stock-delivery-notes-cancel");
    },
    canExecute() {
      return this.document.status === "confirmed" && !this.document.stock_action
        && this.hasPermissions(this.document.direction === "outbound" ? "stock-sales-create" : "stock-receipts-create");
    },
    canInvoice() {
      return this.document.status === "executed" && this.hasPermissions("stock-invoices-create");
    },
    canTransport() {
      return this.document.status === "confirmed"
        && !this.document.stock_transport_order_id
        && this.document.needs_own_transport !== false
        && !this.document.is_intercompany_mirror
        && !this.document.transport_source_delivery_note_id
        && this.hasPermissions("stock-transport-orders-create");
    },
    transportCovered() {
      return this.document.needs_own_transport === false
        || this.document.is_intercompany_mirror
        || !!this.document.transport_source_delivery_note_id;
    },
    statusColor() {
      return { draft: "grey", confirmed: "primary", executed: "success", invoiced: "blue", cancelled: "error" }[this.document.status] || "grey";
    },
    statusLabel() {
      const key = `STOCK.DELIVERY_NOTES.STATUS_${(this.document.status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : this.document.status;
    },
    directionLabel() {
      return this.document.direction === "inbound"
        ? this.$t("STOCK.DELIVERY_NOTES.INBOUND")
        : this.$t("STOCK.DELIVERY_NOTES.OUTBOUND");
    },
    orderCode() {
      return this.document.order_code || this.document.sales_order?.code || this.document.purchase_order?.code;
    },
    deals() {
      return Array.isArray(this.document.deals) ? this.document.deals : [];
    },
    orderRoute() {
      if (this.document.stock_sales_order_id) {
        return { name: "stock-sales-orders-view", params: { id: this.document.stock_sales_order_id } };
      }
      if (this.document.stock_purchase_order_id) {
        return { name: "stock-purchase-orders-view", params: { id: this.document.stock_purchase_order_id } };
      }
      return null;
    },
    transportRoute() {
      const id = this.document.transport_order?.id || this.document.stock_transport_order_id;
      if (!id) {
        return null;
      }
      return { name: "stock-transport-orders-view", params: { id } };
    },
    actionRoute() {
      const action = this.document.stock_action;
      if (!action) {
        return { name: "stock-movements" };
      }
      return this.document.direction === "outbound"
        ? { name: "stock-sales-view", params: { id: action.id } }
        : { name: "stock-receipts-view", params: { id: action.id } };
    },
    linkedInvoices() {
      return Array.isArray(this.document.invoices) ? this.document.invoices : [];
    },
  },
  watch: {
    "$route.params.id"(id, previousId) {
      if (!id || String(id) === String(previousId)) {
        return;
      }
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    dealLabel(deal) {
      if (deal.code && deal.name) {
        return `${deal.code} — ${deal.name}`;
      }
      return deal.code || deal.name || this.$t("STOCK.COMMON.OPEN_DEAL");
    },
    invoiceLabel(invoice) {
      return invoice.fiscal_number || invoice.external_number || invoice.code;
    },
    invoiceStatusLabel(status) {
      const key = `STOCK.STATUSES.INVOICE.${(status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : status;
    },
    invoiceMeta(invoice) {
      const label = this.invoiceLabel(invoice);
      const parts = [];
      if (invoice.code && invoice.code !== label) {
        parts.push(invoice.code);
      }
      if (invoice.external_number && invoice.external_number !== label) {
        parts.push(invoice.external_number);
      }
      const status = this.invoiceStatusLabel(invoice.status);
      if (status) {
        parts.push(status);
      }
      return parts.join(" · ");
    },
    formatStopDate(value) {
      if (!value) {
        return "—";
      }
      const [year, month, day] = String(value).slice(0, 10).split("-");
      if (!year || !month || !day) {
        return value;
      }
      return `${day}/${month}/${year}`;
    },
    async load() {
      this.isProcessing = true;
      try {
        this.document = await this.$store.dispatch(GET_STOCK_DELIVERY_NOTE, this.$route.params.id);
      } finally {
        this.isProcessing = false;
      }
    },
    async save() {
      this.isProcessing = true;
      try {
        const params = { note: this.document.note };
        if (this.canEditPlan) {
          params.etd_date = this.document.etd_date;
          params.eta_date = this.document.eta_date;
          params.items = (this.document.items || []).map((line) => ({
            id: line.id,
            quantity: line.quantity,
            note: line.note,
            net_weight_kg: line.net_weight_kg,
            gross_weight_kg: line.gross_weight_kg,
            pallet_count: line.pallet_count,
          }));
        }
        this.document = await this.$store.dispatch(UPDATE_STOCK_DELIVERY_NOTE, {
          id: this.document.id,
          params,
        });
        this.$store.dispatch(SHOW_SNACK, { message: this.$t("GENERAL.SUCCESS_MESSAGE"), color: "success" });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: errors?.message || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async confirmNote() {
      this.document = await this.$store.dispatch(CONFIRM_STOCK_DELIVERY_NOTE, this.document.id);
    },
    async cancelNote() {
      if (!confirm(this.$t("STOCK.DELIVERY_NOTES.CANCEL_CONFIRM"))) {
        return;
      }
      this.document = await this.$store.dispatch(CANCEL_STOCK_DELIVERY_NOTE, this.document.id);
    },
    async executeNote() {
      this.isProcessing = true;
      const opensReceipt = this.isLeadingPair || this.document.direction === "inbound";
      const noteId = this.isLeadingPair && this.document.paired_delivery_note
        ? this.document.paired_delivery_note.id
        : this.document.id;
      try {
        const action = await this.$store.dispatch(EXECUTE_STOCK_DELIVERY_NOTE, { id: noteId });
        const name = opensReceipt ? "stock-receipts-view" : "stock-sales-view";
        this.$router.push({ name, params: { id: action.id } });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: errors?.message || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async createTransport() {
      const order = await this.$store.dispatch(CREATE_TRANSPORT_ORDER_FROM_DELIVERY_NOTES, {
        delivery_note_ids: [this.document.id],
      });
      this.$router.push({ name: "stock-transport-orders-view", params: { id: order.id } });
    },
    openInvoiceDialog() {
      this.showInvoiceDialog = true;
    },
    handleInvoiceError(errors) {
      this.$store.dispatch(SHOW_SNACK, {
        message: errors?.message || this.$t("GENERAL.UNEXPECTED_ERROR"),
        color: "error",
      });
    },
    handleInvoiceConfirm(options) {
      if (options.openOnly && options.stock_invoice_id) {
        this.showInvoiceDialog = false;
        this.$router.push({
          name: "stock-invoices-view",
          params: { id: options.stock_invoice_id },
        });
        return;
      }
      this.createInvoice(options);
    },
    async createInvoice(options = {}) {
      this.isProcessing = true;
      try {
        const invoice = await this.$store.dispatch(CREATE_STOCK_INVOICE_FROM_DELIVERY_NOTES, {
          delivery_note_ids: [this.document.id],
          stock_invoice_id: options.stock_invoice_id,
          pass_through_extra_costs: options.pass_through_extra_costs || false,
          external_number: options.external_number,
        });
        this.showInvoiceDialog = false;
        this.$router.push({ name: "stock-invoices-view", params: { id: invoice.id } });
      } catch (errors) {
        if (this.$refs.invoiceDialog && this.$refs.invoiceDialog.applySubmitError(errors)) {
          return;
        }
        this.$store.dispatch(SHOW_SNACK, {
          message: errors?.message || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
  },
};
</script>

<style scoped>
.dn-facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
  margin-bottom: 18px;
}

.dn-fact {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  background: #f4f6f6;
  border: 1px solid #e3e8e7;
  border-radius: 8px;
}

.dn-fact__icon {
  margin-top: 1px;
  color: #8b9694 !important;
}

.dn-fact__label {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 3px;
  color: #7a8684;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.dn-fact__label .v-icon {
  color: #a0a8a6 !important;
}

.dn-fact__value {
  color: #2a3332;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
}

.dn-fact__value a {
  color: #1d7f72;
  font-weight: 600;
  text-decoration: none;
}

.dn-fact__meta {
  margin-left: 6px;
  color: #7a8684;
  font-size: 13px;
  font-weight: 500;
  text-transform: capitalize;
}

.dn-route {
  display: flex;
  align-items: stretch;
  gap: 12px;
  margin: 20px 0 22px;
}

.dn-stop {
  flex: 1;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-height: 108px;
  padding: 16px 18px;
  border: 1px solid;
  border-radius: 12px;
}

.dn-stop__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
}

.dn-stop__icon .v-icon {
  color: #fff !important;
}

.dn-stop__body {
  flex: 1;
  min-width: 0;
}

.dn-stop__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.dn-stop__label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.dn-stop__date {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.dn-stop__date-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.dn-stop__date-value {
  font-size: 16px;
  font-weight: 700;
}

.dn-stop__date .v-input {
  max-width: 132px;
  margin-top: 0;
  padding-top: 0;
}

.dn-stop__date .v-text-field input {
  font-size: 16px;
  font-weight: 700;
}

.dn-stop__address {
  color: #1c2423;
  font-size: 17px;
  font-weight: 500;
  line-height: 1.45;
  white-space: pre-line;
}

.dn-stop--loading {
  background: #f1faf8;
  border-color: #b7ddd6;
}

.dn-stop--loading .dn-stop__icon {
  background: #1d7f72;
}

.dn-stop--loading .dn-stop__label,
.dn-stop--loading .dn-stop__date-label,
.dn-stop--loading .dn-stop__date-value,
.dn-stop--loading .dn-stop__date .v-text-field input {
  color: #1d7f72 !important;
}

.dn-stop--unloading {
  background: #fff8ef;
  border-color: #f0d2a8;
}

.dn-stop--unloading .dn-stop__icon {
  background: #d17a12;
}

.dn-stop--unloading .dn-stop__label,
.dn-stop--unloading .dn-stop__date-label,
.dn-stop--unloading .dn-stop__date-value,
.dn-stop--unloading .dn-stop__date .v-text-field input {
  color: #b86a10 !important;
}

.dn-route__arrow {
  display: flex;
  align-items: center;
  color: #1d7f72;
}

@media (max-width: 960px) {
  .dn-route {
    flex-direction: column;
  }

  .dn-route__arrow {
    justify-content: center;
    transform: rotate(90deg);
  }
}
</style>
